export interface ModelPreparationPayload {
  id?: number;
  modelDetailedResult?: number;
  generatedTxt?: string;
  modelPreparationStatusId?: number;
  statusCategory?: string;
  statusDescription?: string;
  isAuthTaxRefund?: boolean;
  isAuthPaidInternationalTransfer?: boolean;
  filingResponse?: string;
  filingCsv?: string;
  filingTime?: string;
  filingExpediente?: string;
  filingJustificante?: string;
  filingUrlPdf?: string;
  deliveryServerDraft?: string;
  deliveryPathDraft?: string;
  deliveryServerFiled?: string;
  deliveryPathFiled?: string;
  resultSelfAssessment?: number;
  isReviewed?: boolean;
  reviewComment?: string;
}

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toBoolean(value: unknown): boolean | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
}

function toStringValue(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const text = String(value).trim();
  return text.length > 0 ? text : undefined;
}

function toSerializedResponse(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'string') {
    return toStringValue(value);
  }

  try {
    const serialized = JSON.stringify(value);
    return serialized === undefined ? undefined : serialized;
  } catch {
    return undefined;
  }
}

function extractId(input: any, field: string): number | undefined {
  const direct = toNumber(input?.[field]);
  if (direct !== undefined) {
    return direct;
  }
  return toNumber(input?.[field]?.id);
}

export function buildModelPreparationPayload(input: any): ModelPreparationPayload {
  const payload: ModelPreparationPayload = {};

  const id = toNumber(input?.id);
  if (id !== undefined) payload.id = id;

  const modelDetailedResult = extractId(input, 'modelDetailedResult');
  if (modelDetailedResult !== undefined) payload.modelDetailedResult = modelDetailedResult;

  const modelPreparationStatusId = extractId(input, 'modelPreparationStatusId') ?? extractId(input, 'modelPreparationStatus');
  if (modelPreparationStatusId !== undefined) payload.modelPreparationStatusId = modelPreparationStatusId;

  const generatedTxt = toStringValue(input?.generatedTxt ?? input?.genratedTxt);
  if (generatedTxt !== undefined) payload.generatedTxt = generatedTxt;

  const statusCategory = toStringValue(input?.statusCategory);
  if (statusCategory !== undefined) payload.statusCategory = statusCategory;

  const statusDescription = toStringValue(input?.statusDescription);
  if (statusDescription !== undefined) payload.statusDescription = statusDescription;

  const isAuthTaxRefund = toBoolean(input?.isAuthTaxRefund);
  if (isAuthTaxRefund !== undefined) payload.isAuthTaxRefund = isAuthTaxRefund;

  const isAuthPaidInternationalTransfer = toBoolean(input?.isAuthPaidInternationalTransfer);
  if (isAuthPaidInternationalTransfer !== undefined) payload.isAuthPaidInternationalTransfer = isAuthPaidInternationalTransfer;

  const filingResponse = toSerializedResponse(input?.filingResponse);
  if (filingResponse !== undefined) payload.filingResponse = filingResponse;

  const filingCsv = toStringValue(input?.filingCsv);
  if (filingCsv !== undefined) payload.filingCsv = filingCsv;

  const filingTime = toStringValue(input?.filingTime);
  if (filingTime !== undefined) payload.filingTime = filingTime;

  const filingExpediente = toStringValue(input?.filingExpediente);
  if (filingExpediente !== undefined) payload.filingExpediente = filingExpediente;

  const filingJustificante = toStringValue(input?.filingJustificante);
  if (filingJustificante !== undefined) payload.filingJustificante = filingJustificante;

  const filingUrlPdf = toStringValue(input?.filingUrlPdf);
  if (filingUrlPdf !== undefined) payload.filingUrlPdf = filingUrlPdf;

  const deliveryServerDraft = toStringValue(input?.deliveryServerDraft);
  if (deliveryServerDraft !== undefined) payload.deliveryServerDraft = deliveryServerDraft;

  const deliveryPathDraft = toStringValue(input?.deliveryPathDraft);
  if (deliveryPathDraft !== undefined) payload.deliveryPathDraft = deliveryPathDraft;

  const deliveryServerFiled = toStringValue(input?.deliveryServerFiled);
  if (deliveryServerFiled !== undefined) payload.deliveryServerFiled = deliveryServerFiled;

  const deliveryPathFiled = toStringValue(input?.deliveryPathFiled);
  if (deliveryPathFiled !== undefined) payload.deliveryPathFiled = deliveryPathFiled;

  const resultSelfAssessment = toNumber(input?.resultSelfAssessment);
  if (resultSelfAssessment !== undefined) payload.resultSelfAssessment = resultSelfAssessment;

  const isReviewed = toBoolean(input?.isReviewed);
  if (isReviewed !== undefined) payload.isReviewed = isReviewed;

  const reviewComment = toStringValue(input?.reviewComment);
  if (reviewComment !== undefined) payload.reviewComment = reviewComment;

  return payload;
}
